import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(null);

  React.useEffect(() => {
    const orgId = new URLSearchParams(window.location.search).get("r");
    if (orgId) api.get(`/auth/org-branding?org_id=${orgId}`).then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(email, password); // PublicOnly then redirects (honouring a safe same-site ?next)
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const forgot = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, a reset link has been generated.");
      setMode("login");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">{branding?.logo_data_url ? <div className="text-center"><img src={branding.logo_data_url} alt="logo" className="h-16 mx-auto object-contain" data-testid="branded-logo" /><div className="mt-2 font-display font-bold text-slate-800">{branding.name}</div></div> : <Logo size="lg" />}</div>
        <div className="cc-card p-7">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{mode === "login" ? "Sign in" : "Reset password"}</h1>
          <p className="text-sm text-slate-500 mt-1">{mode === "login" ? "Welcome back to your kitchen." : "We'll generate a reset link."}</p>

          <form onSubmit={mode === "login" ? submit : forgot} className="space-y-4 mt-6">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12" required />
            </div>
            {mode === "login" && (
              <div>
                <Label className="text-xs font-semibold text-slate-600">Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12" required />
              </div>
            )}
            {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full h-12 bg-brand hover:bg-brand-hover font-bold rounded-xl">
              {busy ? <Loader2 className="animate-spin" /> : mode === "login" ? "Sign in" : "Send reset link"}
            </Button>
          </form>

          {mode === "login" && (
            <>
              <button onClick={() => setMode("forgot")} className="text-xs text-slate-500 hover:text-brand mt-3" data-testid="forgot-link">Forgot password?</button>
              <div className="flex items-center gap-3 my-5"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">or</span><div className="h-px bg-slate-200 flex-1" /></div>
              <Button onClick={googleLogin} variant="outline" data-testid="google-login" className="w-full h-12 rounded-xl font-semibold border-slate-300">
                <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4 mr-2" /> Continue with Google
              </Button>
            </>
          )}
          {mode === "forgot" && <button onClick={() => setMode("login")} className="text-xs text-slate-500 hover:text-brand mt-4" data-testid="back-to-login">← Back to sign in</button>}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-5">Digital kitchen management — accountability built in.</p>
      </div>
    </div>
  );
}
